import { useState } from "react";
import { Phone, Mail, MapPin, Linkedin, Clock, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { CONTACT } from "@/data/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, source: "contact" });
      setDone(true);
      toast.success("Message sent! We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const CONTACTS = [
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref },
    { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref },
    { icon: MapPin, label: "Location", value: CONTACT.location },
    { icon: Linkedin, label: "LinkedIn", value: CONTACT.linkedin, href: CONTACT.linkedinUrl, external: true },
    { icon: Clock, label: "Business hours", value: `${CONTACT.hoursDays}, ${CONTACT.hoursTime}` },
  ];

  return (
    <div>
      <Seo
        title="Contact Quantum Cover | Insurance Help NZ"
        description="Get in touch with Quantum Cover. Friendly insurance help across New Zealand, with support in your language when you need it."
        path="/contact"
      />
      <section className="bg-[#0B3D2E] text-white pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400 mb-3">Contact</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Get in touch</h1>
          <p className="mt-5 text-lg text-slate-300">Got a question? Flick us a message and we'll get back to you within one business day.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-10">
        <div>
          <div className="space-y-5">
            {CONTACTS.map((c) => (
              <div key={c.label} className="flex items-start gap-4">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 shrink-0"><c.icon size={20} /></span>
                <div>
                  <p className="text-sm text-slate-400">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} {...(c.external ? { target: "_blank", rel: "noreferrer" } : {})} className="font-medium text-[#0B3D2E] hover:text-emerald-700 transition-colors" data-testid={`contact-info-${c.label.toLowerCase().split(" ")[0]}`}>{c.value}</a>
                  ) : (
                    <p className="font-medium text-[#0B3D2E]">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-3xl overflow-hidden border border-slate-200 h-56 bg-slate-100 grid place-items-center text-slate-400" data-testid="map-placeholder">
            <div className="text-center"><MapPin size={28} className="mx-auto mb-2" /><p className="text-sm">Auckland, New Zealand</p></div>
          </div>
        </div>

        {done ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)] flex flex-col items-center justify-center text-center" data-testid="contact-success">
            <span className="grid place-items-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600"><Check size={32} /></span>
            <h2 className="mt-6 font-display text-2xl font-semibold text-[#0B3D2E]">Message sent!</h2>
            <p className="mt-2 text-slate-600">Thanks {form.name.split(" ")[0]}, we've got it. We'll get back to you within one business day.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm">Name</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" className="mt-1.5 rounded-xl h-12" /></div>
              <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" className="mt-1.5 rounded-xl h-12" /></div>
            </div>
            <div><Label className="text-sm">Email</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" className="mt-1.5 rounded-xl h-12" /></div>
            <div><Label className="text-sm">Message</Label><Textarea required value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" className="mt-1.5 rounded-xl" rows={5} /></div>
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base">{loading ? "Sending…" : "Send message"}</Button>
          </form>
        )}
      </section>
    </div>
  );
}
